import { Component, EventEmitter, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { IconComponent } from '../../elements/icon/icon.component';
import { Icons } from '../../../model/icon';
import { CommonModule } from '@angular/common';
import { BookingService } from '../../../services/booking2.service';
import { LanguageService } from '../../../services/language.service';
import { CurrencyService } from '../../../services/currency.service';
import { BookingNavigationService } from '../../../services/booking-navigation.service';
import { StorageService } from '../../../services/storage.service';
import { Airport } from '../../../model/airport';
import { BookingPricingService } from '../../../services/booking-pricing.service';

@Component({
  selector: 'app-header-modal',
  standalone: true,
  imports: [
    IconComponent,
    CommonModule
  ],
  templateUrl: './header-modal.component.html',
  styleUrl: './header-modal.component.scss'
})
export class HeaderModalComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Output() close = new EventEmitter<void>();

  isVisible: boolean = false;
  isAnimating: boolean = false;

  protected readonly Icons = Icons;

  constructor(
    public booking: BookingService,
    public languageService: LanguageService,
    public currencyService: CurrencyService,
    private bookingNavigation: BookingNavigationService,
    private storageService: StorageService,
    public bookingPricing: BookingPricingService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen && !this.isVisible) {
        this.openModal();
      } else if (!this.isOpen && this.isVisible) {
        this.closeModal();
      }
    }
  }

  private openModal(): void {
    this.isVisible = true;
    document.body.style.overflow = 'hidden';
  }

  closeModal(): void {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.isVisible = false;

    setTimeout(() => {
      this.isAnimating = false;
      document.body.style.overflow = '';
      this.close.emit();
    }, 300);
  }

  getServicePrice(service: any): number {
    return this.bookingPricing.getServiceTotal(service);
  }

  getTotal(): number {
    return this.bookingPricing.getCartTotal();
  }

  goToCheckout(): void {
    this.closeModal();
    this.bookingNavigation.continueToCheckout();
  }

  getServiceAirport(service: any): Airport | undefined {
    return this.storageService.getAirportsSync().find(
      airport => Number(airport.id) === Number(service.airportId)
    );
  }

  getServiceAirportLine(service: any): string {
    const airport = this.getServiceAirport(service);

    if (!airport) {
      return '';
    }

    const code =
      airport.iata_code ||
      airport.icao_code ||
      airport.faa_code ||
      airport.city_code ||
      '---';

    const country =
      this.languageService.currentLanguage === 'ru'
        ? airport.countryObject?.names?.['ru'] ||
        airport.countryObject?.name ||
        airport.country_code
        : airport.countryObject?.name ||
        airport.country_code;

    return country ? `${code} • ${country}` : code;
  }

  getServiceDateTimeLine(service: any): string {
    const airport = this.getServiceAirport(service);

    if (!airport) {
      return '';
    }

    const routeCode =
      airport.iata_code ||
      airport.icao_code ||
      airport.slug ||
      '';

    const savedForm = routeCode
      ? this.storageService.getAirportBookingForm(routeCode)
      : null;

    const isActiveAirport =
      Number(this.booking.activeAirport()?.id) === Number(airport.id);

    const form = isActiveAirport
      ? this.booking.formData() || savedForm
      : savedForm;

    const date =
      form?.flight?.date ||
      this.booking.data.flight.date;

    const time =
      form?.flight?.time ||
      this.booking.data.flight.departureTime;

    const passengersState =
      form?.passengersState ||
      this.booking.passengers();

    const passengers =
      Number(passengersState?.adult || 0) +
      Number(passengersState?.children || 0) +
      Number(passengersState?.infant || 0);

    return [
      this.formatDate(date),
      this.formatTime(time),
      this.getPassengerText(passengers || 1)
    ].filter(Boolean).join(', ');
  }

  getServiceRoleText(service: any): string {
    const airport = this.getServiceAirport(service);

    switch (airport?.routeRole) {
      case 'departure':
        return this.languageService.translate([
          'Departure service',
          'Услуга вылета'
        ]);

      case 'transit':
        return this.languageService.translate([
          'Transit service',
          'Транзитная услуга'
        ]);

      case 'arrival':
        return this.languageService.translate([
          'Arrival service',
          'Услуга прилета'
        ]);

      default:
        return '';
    }
  }

  private formatDate(date: string): string {
    if (!date) {
      return '';
    }

    const parts = String(date).split('-');

    if (parts.length !== 3) {
      return date;
    }

    const [year, month, day] = parts;

    return `${day}.${month}.${year}`;
  }

  private formatTime(time: string): string {
    return String(time || '').slice(0, 5);
  }

  private getPassengerText(count: number): string {
    if (this.languageService.currentLanguage === 'en') {
      return count === 1
        ? '1 passenger'
        : `${count} passengers`;
    }

    return `${count} ${this.getRussianPassengerWord(count)}`;
  }

  private getRussianPassengerWord(count: number): string {
    const lastTwo = count % 100;
    const lastOne = count % 10;

    if (lastTwo >= 11 && lastTwo <= 14) {
      return 'пассажиров';
    }

    if (lastOne === 1) {
      return 'пассажир';
    }

    if (lastOne >= 2 && lastOne <= 4) {
      return 'пассажира';
    }

    return 'пассажиров';
  }

  getServiceFlightNumber(service: any): string {
    const airport = this.getServiceAirport(service);

    if (!airport) {
      return '';
    }

    const routeCode =
      airport.iata_code ||
      airport.icao_code ||
      airport.slug ||
      '';

    const savedForm = routeCode
      ? this.storageService.getAirportBookingForm(routeCode)
      : null;

    const isActiveAirport =
      Number(this.booking.activeAirport()?.id) === Number(airport.id);

    const form = isActiveAirport
      ? this.booking.formData() || savedForm
      : savedForm;

    return form?.flight?.flightNumber || '';
  }
}
